import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, PLATFORM_ID, ViewChild, inject, input, output, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    turnstile: any;
  }
}

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Shared by every widget on the page, so the script is only requested once.
let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      script.remove();
      reject(new Error('Failed to load Cloudflare Turnstile'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Cloudflare Turnstile check for the forms that register a payment method (DEV-11124).
 *
 * Once its secret is configured, the backend refuses POST /account/payment-method
 * without a valid token, so a bot holding a real session cannot call the endpoint
 * directly to test stolen cards. Tokens are single use: call `reset()` after every
 * submit. Renders nothing when the environment has no site key.
 */
@Component({
  selector: 'app-turnstile',
  imports: [],
  template: `<div #container class="d-flex justify-content-center"></div>`
})
export class TurnstileComponent implements AfterViewInit, OnDestroy {

  static readonly enabled = !!environment.apis.turnstile.siteKey;

  @ViewChild('container', { static: true }) container!: ElementRef<HTMLElement>;

  /** A fresh token, or null when the previous one expired or the check failed. */
  outToken = output<string | null>();

  /**
   * Labels the widget in Cloudflare's analytics, so each protected form can be read — and
   * rate-limited — on its own. Cloudflare only accepts `[a-zA-Z0-9_-]{1,32}`.
   */
  action = input<string>('payment_method');

  /**
   * True once Cloudflare decides this visitor has to click the widget. With
   * `interaction-only` the check is invisible until that happens, so a form waiting for a
   * token needs to know which of the two it is: still verifying, or waiting on the customer.
   */
  readonly isInteractive = signal(false);

  #platformId = inject(PLATFORM_ID);
  #zone = inject(NgZone);
  #widgetId: string | null = null;
  #destroyed = false;

  ngAfterViewInit(): void {
    if (!TurnstileComponent.enabled || !isPlatformBrowser(this.#platformId)) return;

    loadTurnstileScript()
      .then(() => this.#render())
      .catch(error => console.error(error));
  }

  reset(): void {
    this.outToken.emit(null);
    this.isInteractive.set(false);
    if (this.#widgetId !== null) window.turnstile?.reset(this.#widgetId);
  }

  ngOnDestroy(): void {
    this.#destroyed = true;
    if (this.#widgetId !== null) window.turnstile?.remove(this.#widgetId);
  }

  #render(): void {
    if (this.#destroyed) return;

    this.#widgetId = window.turnstile.render(this.container.nativeElement, {
      sitekey: environment.apis.turnstile.siteKey,
      action: this.action(),
      // Only shows up when Cloudflare needs the visitor to interact.
      appearance: 'interaction-only',
      'before-interactive-callback': () => this.#zone.run(() => this.isInteractive.set(true)),
      callback: (token: string) => this.#zone.run(() => {
        this.isInteractive.set(false);
        this.outToken.emit(token);
      }),
      'expired-callback': () => this.#zone.run(() => this.outToken.emit(null)),
      'error-callback': () => this.#zone.run(() => this.outToken.emit(null)),
    });
  }
}
